import { useState } from 'react';
import type { Step, StepAnswer } from '../../../types/step';

interface RewardModalProps {
  step: Step;
  answers?: StepAnswer;
  onClose: () => void;
}

export default function RewardModal({ step, answers, onClose }: RewardModalProps) {
  const [showAnalysis, setShowAnalysis] = useState(false);

  const getAnalysisText = () => {
    const responses = answers?.responses || [];
    const hasContent = responses.some(r => r && r.trim().length > 0);

    if (!hasContent) {
      return 'Ты сделала важный шаг! Даже если пока не все слова найдены — ты двигаешься вперёд, и это главное.';
    }

    const analysisMap: Record<number, string> = {
      1: `Какой невероятный путь ты прошла! ${responses[0] ? 'Твоё начало показывает смелость — ты не боялась пробовать.' : ''} ${responses[1] ? 'Сложности, через которые ты прошла, закалили тебя.' : ''} ${responses[2] ? 'Момент осознания своего роста — это поворотная точка, которую многие не замечают, но ты её увидела!' : ''} ${responses[3] ? 'То, чем ты гордишься сейчас — это результат твоей настойчивости и веры в себя.' : ''} Ты уже не та, кем была в начале. Ты выросла, и это видно.`,
      
      2: `Твоя ценность огромна! ${responses[0] ? 'Благодарность, которую ты получаешь — это прямое отражение твоего вклада.' : ''} ${responses[1] ? 'Твоя польза для команды реальна и ощутима.' : ''} ${responses[2] ? 'То, что ты умеешь делать лучше других — это твоя уникальная сила.' : ''} Ты не просто выполняешь задачи — ты создаёшь ценность, которую видят и чувствуют другие. Это дорогого стоит.`,
      
      3: `Твой стиль управления — это твоя подпись! ${responses[0] ? 'Способ, которым ты принимаешь решения, показывает твою зрелость.' : ''} ${responses[1] ? 'Твоя реакция на хаос говорит о твоей силе и устойчивости.' : ''} ${responses[2] ? 'То, как ты ведёшь людей — это искусство, которое ты развиваешь каждый день.' : ''} Не копируй других — твой стиль уникален, и именно он делает тебя эффективной.`,
      
      4: `Какая сила в твоих границах! ${responses[0] ? 'То, что ты больше не готова принимать — это не жёсткость, это самоуважение.' : ''} ${responses[1] ? 'Места, где ты больше не молчишь — это территория твоей силы.' : ''} ${responses[2] ? 'Границы, которые ты назвала — это фундамент твоего благополучия.' : ''} Ты учишься говорить «нет» тому, что тебя истощает. Это акт любви к себе и к своему делу.`,
      
      5: `Ты настоящая героиня! ${responses[0] ? 'Ситуации, где ты справилась — это доказательство твоей силы.' : ''} ${responses[1] ? 'Моменты, когда ты была сильной — это твои победы, даже если они казались незаметными.' : ''} Часто мы не видим свои достижения, но они есть. Ты справляешься каждый день, и это невероятно!`,
      
      6: `Твоя энергия — это твой самый ценный ресурс! ${responses[0] ? 'То, что тебя наполняет — это твой источник силы, береги его.' : ''} ${responses[1] ? 'То, что тебя истощает — важно видеть, чтобы защищаться.' : ''} ${responses[2] ? 'То, что ты хочешь беречь — это твоя мудрость.' : ''} Ты начинаешь понимать свою энергию, и это ключ к долгой и счастливой карьере.`,
      
      7: `Твой голос имеет силу! ${responses[0] ? 'То, как ты говоришь «нет» сейчас — это уже шаг.' : ''} ${responses[1] ? 'То, как ты хочешь говорить «нет» — это твоё право.' : ''} ${responses[2] ? 'То, как ты хочешь, чтобы с тобой разговаривали — это твоя граница.' : ''} Твой голос заслуживает быть услышанным. Ты имеешь право на уважение и внимание.`,
      
      8: `Твоя уверенность растёт! ${responses[0] ? 'То, в чём ты уверена — это твоя опора.' : ''} ${responses[1] ? 'Сомнения — это нормально, они показывают, что ты думаешь и анализируешь.' : ''} ${responses[2] ? 'То, что могло бы усилить твою уверенность — это твой план роста.' : ''} Уверенность — это не отсутствие страха, это умение действовать несмотря на него. И ты это делаешь!`,
      
      9: `Твоё влияние реально! ${responses[0] ? 'Люди, которым ты помогаешь — это твой след в мире.' : ''} ${responses[1] ? 'То, как ты меняешь пространство вокруг себя — это твоя сила.' : ''} Ты не просто работаешь — ты меняешь жизни людей вокруг. Это огромная ценность!`,
      
      10: `Твои роли — это твоя многогранность! ${responses[0] ? 'Роли, которые ты играешь, показывают твою гибкость.' : ''} ${responses[1] ? 'Роль, которая утомляет — важно её видеть.' : ''} ${responses[2] ? 'Роль, которая наполняет — это твоё призвание.' : ''} Ты можешь выбирать, какие роли усиливать, а какие — отпускать. Это твоя власть.`,
      
      11: `Твои желания — это твой компас! ${responses[0] ? 'То, чего ты хочешь больше — это направление твоего роста.' : ''} ${responses[1] ? 'То, чего ты хочешь меньше — это твоя мудрость.' : ''} Ты знаешь, чего хочешь. Это уже половина пути к достижению!`,
      
      12: `У тебя есть опора! ${responses[0] ? 'То, на что ты можешь опереться — это твоя сила.' : ''} ${responses[1] ? 'Люди, которые тебя поддерживают — это твоё богатство.' : ''} Ты не одна. У тебя есть ресурсы, и ты умеешь их использовать. Это признак зрелости!`,
      
      13: `Твоё будущее начинается сейчас! ${responses[0] ? 'Видение себя через год — это твоя цель.' : ''} ${responses[1] ? 'То, как ты хочешь жить — это твой выбор.' : ''} Ты создаёшь своё будущее каждым решением. И оно будет прекрасным, потому что ты его выбираешь!`,
      
      14: `Твои правила — это твоя система координат! ${responses[0] ? 'Правила, которые ты создала — это твоя мудрость.' : ''} Они помогут тебе не сбиться с пути. Ты знаешь, как ты хочешь жить и работать. Это сила!`,
      
      15: `Ты прошла весь путь! ${responses[0] ? 'Ты знаешь, кто ты — это основа всего.' : ''} ${responses[1] ? 'Ты выбрала, как жить и работать — это твоя свобода.' : ''} ${responses[2] ? 'Ты видишь свою силу — это твоя уверенность.' : ''} Теперь у тебя есть твой манифест. Это не просто слова — это твоя правда, твоя опора, твоя сила. Ты невероятная!`
    };

    return analysisMap[step.id] || 'Ты сделала важный шаг на своём пути! Каждый ответ — это твоя честность с собой, и это дорогого стоит.';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] p-8 rounded-t-3xl text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-star-fill text-5xl text-white"></i>
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">
            Шаг {step.id} пройден!
          </h2>
          <p className="text-white/90 text-lg">
            {step.reward}
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {!showAnalysis ? (
            <>
              <div className="text-center mb-6">
                <p className="text-gray-600 text-lg mb-4">
                  Хочешь узнать, что говорят твои ответы о тебе?
                </p>
                <button
                  onClick={() => setShowAnalysis(true)}
                  className="px-8 py-4 bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] text-white rounded-2xl font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  Показать анализ
                </button>
              </div>
              
              <div className="pt-6 border-t border-gray-100">
                <button
                  onClick={onClose}
                  className="w-full px-6 py-4 bg-gray-900 text-white rounded-2xl font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Продолжить путь
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-gradient-to-br from-[#F9659C]/5 to-[#ACE0EB]/5 rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Твой анализ
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {getAnalysisText()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#ACE0EB]/10 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <i className="ri-lightbulb-fill text-2xl text-[#F9659C]"></i>
                  <h4 className="font-bold text-gray-900">Что это значит?</h4>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Каждый твой ответ — это шаг к большей осознанности. Ты не просто заполняешь поля — ты создаёшь карту своей силы. Продолжай, впереди ещё много открытий!
                </p>
              </div>

              <button
                onClick={onClose}
                className="w-full px-6 py-4 bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] text-white rounded-2xl font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Продолжить путь
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
